import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { useBranding, assetUrl } from "../lib/useBranding";
import ReportErrorButton from "./ReportErrorButton";

const FooterLogo = () => {
  const branding = useBranding();
  const logoUrl = assetUrl(branding.logo_url)
    || assetUrl(branding.logo_url_light)
    || "/icon-512.png";
  return (
    <Link to="/" data-testid="footer-logo" aria-label="Talentsio home" className="inline-flex items-center select-none">
      <img src={logoUrl} alt="Talentsio" data-testid="footer-logo-img"
        className="h-9 w-auto max-w-[180px] object-contain" />
    </Link>
  );
};

// Brief 6 §2 — Browse block (3 columns): by type / for / by city.
// The category slugs use the ARTIST URL structure (kept for SEO) even though
// the customer-facing label uses "talang" in Swedish.
const BROWSE = {
  byType: {
    en: [
      { label: "DJs", path: "/book/dj" },
      { label: "Bands", path: "/book/band" },
      { label: "Cover bands", path: "/book/coverband" },
      { label: "Solo artists", path: "/book/singer" },
      { label: "Duos", path: "/book/duo" },
      { label: "Trios", path: "/book/trio" },
      { label: "Acoustic acts", path: "/book/acoustic" },
      { label: "Jazz ensembles", path: "/book/jazz" },
    ],
    sv: [
      { label: "DJ:s", path: "/sv/boka/dj" },
      { label: "Band", path: "/sv/boka/band" },
      { label: "Coverband", path: "/sv/boka/coverband" },
      { label: "Soloartister", path: "/sv/boka/singer" },
      { label: "Duos", path: "/sv/boka/duo" },
      { label: "Trios", path: "/sv/boka/trio" },
      { label: "Akustiska akter", path: "/sv/boka/acoustic" },
      { label: "Jazzensembler", path: "/sv/boka/jazz" },
    ],
  },
  bookFor: {
    en: [
      { label: "Weddings", path: "/boka?event_type=wedding" },
      { label: "Corporate events", path: "/boka?event_type=corporate_event" },
      { label: "Private parties", path: "/boka?event_type=private_party" },
      { label: "Birthday parties", path: "/boka?event_type=birthday_party" },
      { label: "Festivals", path: "/boka?event_type=festival" },
      { label: "Product launches", path: "/boka?event_type=product_launch" },
      { label: "Christmas parties", path: "/boka?event_type=christmas_party" },
      { label: "Gala or awards", path: "/boka?event_type=gala_awards" },
    ],
    sv: [
      { label: "Bröllop", path: "/boka?event_type=wedding" },
      { label: "Företagsevent", path: "/boka?event_type=corporate_event" },
      { label: "Privata fester", path: "/boka?event_type=private_party" },
      { label: "Födelsedagsfester", path: "/boka?event_type=birthday_party" },
      { label: "Festivaler", path: "/boka?event_type=festival" },
      { label: "Produktlanseringar", path: "/boka?event_type=product_launch" },
      { label: "Julfester", path: "/boka?event_type=christmas_party" },
      { label: "Gala eller utmärkelse", path: "/boka?event_type=gala_awards" },
    ],
  },
  byCity: {
    en: [
      { label: "Stockholm", path: "/book/dj/stockholm" },
      { label: "Göteborg", path: "/book/dj/goteborg" },
      { label: "Malmö", path: "/book/dj/malmo" },
      { label: "Uppsala", path: "/book/dj/uppsala" },
      { label: "Linköping", path: "/book/dj/linkoping" },
      { label: "Örebro", path: "/book/dj/orebro" },
      { label: "Helsingborg", path: "/book/dj/helsingborg" },
      { label: "Jönköping", path: "/book/dj/jonkoping" },
    ],
    sv: [
      { label: "Stockholm", path: "/sv/boka/dj/stockholm" },
      { label: "Göteborg", path: "/sv/boka/dj/goteborg" },
      { label: "Malmö", path: "/sv/boka/dj/malmo" },
      { label: "Uppsala", path: "/sv/boka/dj/uppsala" },
      { label: "Linköping", path: "/sv/boka/dj/linkoping" },
      { label: "Örebro", path: "/sv/boka/dj/orebro" },
      { label: "Helsingborg", path: "/sv/boka/dj/helsingborg" },
      { label: "Jönköping", path: "/sv/boka/dj/jonkoping" },
    ],
  },
};

const BrowseColumn = ({ title, items, testId }) => (
  <div data-testid={testId}>
    <h4 className="text-[13px] font-semibold tracking-[0.1em] uppercase text-neutral-500">{title}</h4>
    <ul className="mt-4 space-y-2.5">
      {items.map((it) => (
        <li key={it.label}>
          <Link to={it.path} className="text-[14px] text-neutral-400 hover:text-white transition-colors">{it.label}</Link>
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const { t, lang } = useLang();
  const sv = lang === "sv";
  const _B = sv
    ? { byType: BROWSE.byType.sv, bookFor: BROWSE.bookFor.sv, byCity: BROWSE.byCity.sv }
    : { byType: BROWSE.byType.en, bookFor: BROWSE.bookFor.en, byCity: BROWSE.byCity.en };

  const platformCol = {
    title: sv ? "Plattform" : "Platform",
    items: [
      [sv ? "Så fungerar det" : "How it works", sv ? "/sv/how-it-works" : "/how-it-works"],
      [sv ? "Priser" : "Pricing", sv ? "/sv/for-talents#pricing" : "/for-talents#pricing"],
      ["Spotlight", "/spotlight"],
      [sv ? "För talanger" : "For talents", sv ? "/sv/for-talents" : "/for-talents"],
    ],
  };
  const companyCol = {
    title: sv ? "Företag" : "Company",
    items: [
      [sv ? "Om oss" : "About", sv ? "/sv/about" : "/about"],
      [sv ? "Guider" : "Guides", "/guides"],
      [sv ? "Kontakt" : "Contact", sv ? "/sv/contact" : "/contact"],
      [sv ? "Press" : "Press", "/press"],
    ],
  };
  const legalCol = {
    title: sv ? "Juridik" : "Legal",
    items: [
      [sv ? "Användarvillkor" : "Terms of service", sv ? "/sv/terms" : "/terms"],
      [sv ? "Integritetspolicy" : "Privacy policy", sv ? "/sv/privacy" : "/privacy"],
      [sv ? "Cookies" : "Cookies", null],
      ["DAC7", sv ? "/sv/dac7" : "/dac7"],
    ],
  };
  const supportCol = {
    title: sv ? "Support" : "Support",
    items: [
      [sv ? "Vanliga frågor" : "FAQ", "/faq"],
      ["hello@talentsio.com", "mailto:hello@talentsio.com"],
      [sv ? "Hjälpcenter" : "Help center", "/support"],
    ],
  };
  const navCols = [platformCol, companyCol, legalCol, supportCol];

  return (
    <footer className="border-t border-[#1e1e1e] pt-16 pb-8">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Row 1 — Browse block (3 columns) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-12 border-b border-[#1e1e1e]" data-testid="footer-browse-block">
          <BrowseColumn title={sv ? "Boka efter typ" : "Book by type"}    items={_B.byType}  testId="footer-browse-by-type" />
          <BrowseColumn title={sv ? "Boka till"      : "Book for"}         items={_B.bookFor} testId="footer-browse-book-for" />
          <BrowseColumn title={sv ? "Boka efter stad": "Browse by city"}   items={_B.byCity}  testId="footer-browse-by-city" />
        </div>

        {/* Row 2 — Nav block (4 columns) */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10 pt-12" data-testid="footer-nav-block">
          <div>
            <FooterLogo />
            <p className="mt-4 text-[14px] text-neutral-500 max-w-[220px]">{t("footer.tagline")}</p>
          </div>
          {navCols.map((col) => (
            <div key={col.title}>
              <h4 className="text-[13px] font-semibold tracking-[0.1em] uppercase text-neutral-500">{col.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {col.items.map(([label, to]) => (
                  <li key={label}>
                    {to === null
                      ? <button data-testid="footer-cookie-settings" onClick={() => window.dispatchEvent(new Event("open_cookie_settings"))} className="text-[14px] text-neutral-400 hover:text-white transition-colors">{label}</button>
                      : to.startsWith("mailto:") || to.startsWith("http")
                        ? <a href={to} className="text-[14px] text-neutral-400 hover:text-white transition-colors">{label}</a>
                        : <Link to={to} className="text-[14px] text-neutral-400 hover:text-white transition-colors">{label}</Link>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {/* Row 3 — Copyright */}
        <div className="mt-12 pt-6 border-t border-[#1e1e1e] flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[13px] text-neutral-600" data-testid="footer-copyright">
            © 2026 Talentsio. {sv ? "Gjort i Sverige." : "Made in Sweden."}
          </p>
          <ReportErrorButton
            context="footer"
            label={sv ? "Rapportera fel" : "Report a bug"}
            testId="footer-report-bug"
            className="text-[13px] text-neutral-500 hover:text-[#FF5C00] transition-colors inline-flex items-center gap-1.5"
          />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
